/**
 * A distance chip's search — "Inom 50 km" (P25009-425 BR-2).
 *
 * The Marketplace API filters on a box (`bounds`), not a circle, so a box
 * around the circle is what it is asked for. Its corners reach 1.4 × the
 * radius: a card reading "ca 68 km" under "Inom 50 km" would be plainly wrong.
 * So, like the car's fit search (carFitSearch.js), the box's listings are
 * fetched with only their position, trimmed to the circle, and only then
 * paged — the count and the pages are the circle's. A box holds a few hundred
 * listings at most; past FIT_CANDIDATE_MAX_PAGES the box's first 1,000 in sort
 * order are used and `truncated` says so.
 */
import { fetchCandidates, isDisplayParam } from './carFitSearch';
import { boundsAroundKm, isWithinRadius } from '../../util/buyerArea';

/** The `bounds` value for a radius search, or null. */
export const radiusBounds = radius =>
  radius ? boundsAroundKm(radius.center, radius.km) : null;

/** The circle test for one listing (needs `geolocation` in the response). */
export const withinRadiusOf = radius => listing =>
  isWithinRadius(radius.center, listing?.attributes?.geolocation, radius.km);

/**
 * Run one radius search and return a page of it.
 *
 * @param {Object} options
 * @param {Object} options.sdk
 * @param {Object} options.params  the search's query params, `bounds` included
 * @param {{center: {lat, lng}, km: Number}} options.radius
 * @param {number} [options.page]  1-based
 * @param {number} [options.perPage]
 * @returns {Promise<Object>} shaped like an SDK listings.query response, with
 *   `radius: { checked, truncated }` beside `data`
 */
export const queryWithinRadius = async ({ sdk, params, radius, page = 1, perPage }) => {
  const filters = Object.fromEntries(Object.entries(params).filter(([k]) => !isDisplayParam(k)));
  const display = Object.fromEntries(Object.entries(params).filter(([k]) => isDisplayParam(k)));
  delete filters.page;
  delete filters.perPage;

  const candidates = await fetchCandidates(sdk, { ...filters, 'fields.listing': ['geolocation'] });
  const ids = candidates.listings.filter(withinRadiusOf(radius)).map(l => l.id);

  const size = perPage || ids.length || 1;
  const pageIds = ids.slice((page - 1) * size, page * size);
  let data = [];
  let included = [];
  if (pageIds.length > 0) {
    const full = await sdk.listings.query({ ...display, ids: pageIds, perPage: pageIds.length });
    const byId = new Map((full.data.data || []).map(l => [l.id.uuid, l]));
    data = pageIds.map(id => byId.get(id.uuid)).filter(Boolean);
    included = full.data.included || [];
  }

  return {
    data: {
      data,
      included,
      meta: {
        totalItems: ids.length,
        totalPages: Math.max(1, Math.ceil(ids.length / size)),
        page,
        perPage: size,
      },
    },
    radius: { checked: candidates.listings.length, truncated: candidates.truncated },
  };
};
